import React, { useState, useEffect, startTransition, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  LinearProgress,
  IconButton,
  Paper,
  ListItemText,
  InputLabel,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Tabs,
  Tab,
  Popover
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import { toast } from "react-hot-toast";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortIcon from "@mui/icons-material/Sort";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableItem = ({ id, title }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.5,
        mb: 1,
        border: `1px solid ${COLORS.BORDER || "#ECE7F2"}`,
        borderRadius: "8px",
        backgroundColor: isDragging ? "#f5f5f5" : "#fff",
      }}
    >
      <Box {...attributes} {...listeners} sx={{ cursor: "grab", mr: 2, display: "flex", alignItems: "center" }}>
        <DragIndicatorIcon color="action" />
      </Box>
      <Typography sx={{ fontWeight: 500, color: COLORS.SECONDARY }}>{title}</Typography>
    </Paper>
  );
};

const TargetNodeItem = ({ node, invalidIds, selectedId, onSelect, depth }) => {
  const [expanded, setExpanded] = useState(true);
  const isInvalid = invalidIds.includes(node.id);
  const isSelected = selectedId === node.id;
  const hasChildren = node.subItems && node.subItems.length > 0;
  
  if (isInvalid) return null;

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex", alignItems: "center",
          py: 0.5, pl: depth * 2, pr: 1,
          cursor: "pointer",
          bgcolor: isSelected ? "rgba(110, 56, 150, 0.08)" : "transparent",
          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
          borderRadius: "4px"
        }}
        onClick={() => onSelect(node.id)}
      >
        <Box sx={{ width: 24, display: "flex", justifyContent: "center" }}>
          {hasChildren && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} sx={{ p: 0.2 }}>
              {expanded ? <KeyboardDoubleArrowUpIcon sx={{ fontSize: 14 }}/> : <KeyboardDoubleArrowDownIcon sx={{ fontSize: 14 }}/>}
            </IconButton>
          )}
        </Box>
        <Typography sx={{ fontSize: "13px", fontWeight: isSelected ? 700 : 500, color: isSelected ? COLORS.PRIMARY : COLORS.TEXT }}>
          {node.title}
        </Typography>
      </Box>
      {hasChildren && expanded && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {node.subItems.map(child => (
            <TargetNodeItem key={child.id} node={child} invalidIds={invalidIds} selectedId={selectedId} onSelect={onSelect} depth={depth + 1} />
          ))}
        </Box>
      )}
    </React.Fragment>
  );
};

/* ---------------- DYNAMIC NODE BUILDER ---------------- */
const DynamicNodeBuilder = ({
  node,
  onDelete,
  onDetailChange,
  onAddSibling, // Add this
  onAddChild,
  onAddCustomSibling,
  onAddCustomChild,
  onCustomDetailChange,
  onAddCustomDetail,
  onRemoveCustomDetail,
  onRemoveMasterDetail,
  onTitleChange,
  sourceOptions,
  expandTrigger,
  collapseTrigger,
  onCopy,
  onPaste,
  hasCopiedNode,
  level = 0,
  parentChildOptions,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onOpenSort,
  masterNodeMap,
}) => {
  const [isExpanded, setIsExpanded] = useState(level < 1);
  const [selectedSiblings, setSelectedSiblings] = useState([]);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [siblingDropdownOpen, setSiblingDropdownOpen] = useState(false);
  const [childDropdownOpen, setChildDropdownOpen] = useState(false);

  useEffect(() => {
    if (expandTrigger > 0) setIsExpanded(true);
  }, [expandTrigger]);

  useEffect(() => {
    if (collapseTrigger > 0) setIsExpanded(false);
  }, [collapseTrigger]);

  const subItemsLength = node.subItems ? node.subItems.length : 0;
  const prevSubItemsLength = React.useRef(subItemsLength);

  useEffect(() => {
    if (subItemsLength > prevSubItemsLength.current) {
      setIsExpanded(true);
    }
    prevSubItemsLength.current = subItemsLength;
  }, [subItemsLength]);

  // Use the pre-computed map for O(1) lookup
  let masterNode = masterNodeMap ? masterNodeMap.get(String(node.masterId)) : null;
  
  // Fallback: if masterId failed (e.g. DB was reseeded with new IDs), try matching by title
  if (!masterNode && masterNodeMap && node.title) {
    for (const [id, mNode] of masterNodeMap.entries()) {
      if (mNode.title === node.title) {
        masterNode = mNode;
        break;
      }
    }
  }
  
  const childOptions = masterNode?.subItems || [];

  // Sibling options: If level 0, all top-level from all sources.
  // Else, the siblings of THIS node's master node in ITS parent.
  // Sibling options are exactly the parent's child options.
  // For top-level items, parentChildOptions will be all top-level components from sources.
  const siblingOptions = parentChildOptions || [];

  return (
    <Box sx={{ mb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: `1px solid ${COLORS.BORDER || "#ECE7F2"}`,
          borderRadius: "10px",
          backgroundColor: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            {node.subItems && node.subItems.length > 0 && (
              <IconButton
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ color: COLORS.PRIMARY, p: 0.5, mt: 0.5 }}
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
            )}
            {(!node.subItems || node.subItems.length === 0) && (
              <Box sx={{ width: 32 }} />
            )}
            <Box>
            {/* Title is always editable so cloned/pasted items can be renamed */}
            <TextField
              size="small"
              variant="standard"
              value={node.title}
              onChange={(e) => onTitleChange(node.id, e.target.value)}
              placeholder="Component Title"
              sx={{
                "& .MuiInput-root": {
                  ...TYPOGRAPHY.H4,
                  color: COLORS.SECONDARY,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                },
              }}
            />
            <Typography
              sx={{
                fontSize: "11px",
                color: COLORS.MUTED_TEXT,
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Level {level + 1} Element
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Move Up">
            <span>
              <IconButton 
                size="small" 
                color="primary" 
                onClick={() => onMoveUp(node.id)}
                disabled={isFirst}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move Down">
            <span>
              <IconButton 
                size="small" 
                color="primary" 
                onClick={() => onMoveDown(node.id)}
                disabled={isLast}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Sort Same Level Items">
            <IconButton 
              size="small" 
              color="primary" 
              onClick={() => onOpenSort(node.id)}
            >
              <SortIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy / Clone Component">
            <IconButton size="small" color="primary" onClick={() => onCopy(node)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(node)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

        {/* Metadata Inputs */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
          {node.masterExtraDetails.map((key, idx) => (
            <Box key={idx} sx={{ width: "calc(50% - 8px)", minWidth: "150px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: COLORS.PRIMARY,
                  }}
                >
                  {key}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newDetails = [...node.extraDetails];
                    const newKeys = [...node.masterExtraDetails];
                    newDetails.splice(idx, 1);
                    newKeys.splice(idx, 1);
                    
                    // We need a way to update BOTH in the parent
                    // I will modify onDetailChange to handle deletion if value is null or something
                    // Or better, I'll implement handleRemoveMasterDetail in the parent
                    onRemoveMasterDetail(node.id, idx);
                  }}
                  sx={{ p: 0, opacity: 0.5, "&:hover": { opacity: 1 }, color: "error.main" }}
                >
                  <CloseIcon sx={{ fontSize: "12px" }} />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder={node.placeholderDetails?.[idx] || `Enter ${key}...`}
                value={node.extraDetails[idx] || ""}
                onChange={(e) => onDetailChange(node.id, idx, e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: "#F9F9FB",
                  },
                }}
              />
            </Box>
          ))}
          {node.masterExtraDetails.length === 0 && !node.customExtraDetails?.length && (
            <Typography
              sx={{
                fontSize: "12px",
                color: COLORS.MUTED_TEXT,
                fontStyle: "italic",
              }}
            >
              No extra details defined for this element.
            </Typography>
          )}
        </Box>

        {/* Custom Extra Details */}
        <Box sx={{ mb: 2 }}>
          {(node.customExtraDetails || []).map((detail, idx) => (
            <Box key={`custom-${idx}`} sx={{ display: "flex", gap: 2, width: "100%", alignItems: "flex-end", mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "12px", mb: 0.5, fontWeight: 600, color: COLORS.PRIMARY }}>
                  Detail Name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Area"
                  value={detail.key}
                  onChange={(e) => onCustomDetailChange(node.id, idx, "key", e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", backgroundColor: "#F9F9FB" } }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "12px", mb: 0.5, fontWeight: 600, color: COLORS.PRIMARY }}>
                  Value
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter value..."
                  value={detail.value}
                  onChange={(e) => onCustomDetailChange(node.id, idx, "value", e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", backgroundColor: "#F9F9FB" } }}
                />
              </Box>
              <IconButton size="small" color="error" onClick={() => onRemoveCustomDetail(node.id, idx)} sx={{ mb: 0.5 }}>
                <CloseIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Box>
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddCustomDetail(node.id)}
            sx={{ color: COLORS.PRIMARY, fontSize: "12px", textTransform: "none", fontWeight: 600 }}
          >
            Add Extra Detail
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl size="small" sx={{ width: "190px" }}>
            <Select
              multiple
              displayEmpty
              open={siblingDropdownOpen}
              onOpen={() => setSiblingDropdownOpen(true)}
              onClose={() => setSiblingDropdownOpen(false)}
              value={selectedSiblings}
              onChange={(e) => {
                const val = e.target.value;
                if (val.includes("custom")) {
                  onAddCustomSibling(node.id);
                  setSelectedSiblings(val.filter(v => v !== "custom" && v !== "selectAll"));
                  setSiblingDropdownOpen(false);
                } else if (val.includes("selectAll")) {
                  if (selectedSiblings.length === siblingOptions.length) {
                    setSelectedSiblings([]);
                  } else {
                    setSelectedSiblings(siblingOptions);
                  }
                } else {
                  setSelectedSiblings(val);
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: { width: "220px", maxWidth: "220px" },
                },
              }}
              renderValue={() => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AddIcon sx={{ fontSize: "16px" }} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                    Add at Same Level
                  </Typography>
                </Box>
              )}
              sx={{ borderRadius: "6px", height: "36px" }}
            >
              {siblingOptions.length > 0 && (
                <MenuItem value="selectAll" sx={{ fontWeight: 600 }}>
                  <Checkbox 
                    checked={selectedSiblings.length === siblingOptions.length && siblingOptions.length > 0} 
                    indeterminate={selectedSiblings.length > 0 && selectedSiblings.length < siblingOptions.length}
                    size="small" 
                    sx={{ p: 0.5, mr: 1 }} 
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
              )}
              {siblingOptions.map((opt) => (
                <MenuItem key={opt._id} value={opt} sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  <Checkbox checked={selectedSiblings.findIndex((s) => s._id === opt._id) > -1} size="small" sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={opt.title} />
                </MenuItem>
              ))}
              <Divider />
              <MenuItem value="custom" sx={{ color: COLORS.PRIMARY, fontWeight: 600, whiteSpace: "normal" }}>
                + Add Custom Component
              </MenuItem>
              {selectedSiblings.length > 0 && (
                <Box sx={{ p: 1, position: 'sticky', bottom: 0, bgcolor: 'white', zIndex: 1, borderTop: '1px solid #eee' }}>
                  <Button fullWidth variant="contained" size="small" onClick={(e) => {
                    e.stopPropagation();
                    onAddSibling(node.id, selectedSiblings);
                    setSelectedSiblings([]);
                    setSiblingDropdownOpen(false);
                  }}>
                    Add {selectedSiblings.length} Selected
                  </Button>
                </Box>
              )}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: "190px" }}>
            <Select
              multiple
              displayEmpty
              open={childDropdownOpen}
              onOpen={() => setChildDropdownOpen(true)}
              onClose={() => setChildDropdownOpen(false)}
              value={selectedChildren}
              onChange={(e) => {
                const val = e.target.value;
                if (val.includes("custom")) {
                  onAddCustomChild(node.id);
                  setSelectedChildren(val.filter(v => v !== "custom" && v !== "selectAll"));
                  setChildDropdownOpen(false);
                } else if (val.includes("selectAll")) {
                  if (selectedChildren.length === childOptions.length) {
                    setSelectedChildren([]);
                  } else {
                    setSelectedChildren(childOptions);
                  }
                } else {
                  setSelectedChildren(val);
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: { width: "220px", maxWidth: "220px" },
                },
              }}
              renderValue={() => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SubdirectoryArrowRightIcon sx={{ fontSize: "16px" }} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                    Add at Sub Level
                  </Typography>
                </Box>
              )}
              sx={{ borderRadius: "6px", height: "36px" }}
            >
              {childOptions.length > 0 && (
                <MenuItem value="selectAll" sx={{ fontWeight: 600 }}>
                  <Checkbox 
                    checked={selectedChildren.length === childOptions.length && childOptions.length > 0} 
                    indeterminate={selectedChildren.length > 0 && selectedChildren.length < childOptions.length}
                    size="small" 
                    sx={{ p: 0.5, mr: 1 }} 
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
              )}
              {childOptions.map((opt) => (
                <MenuItem key={opt._id} value={opt} sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  <Checkbox checked={selectedChildren.findIndex((s) => s._id === opt._id) > -1} size="small" sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={opt.title} />
                </MenuItem>
              ))}
              <Divider />
              <MenuItem value="custom" sx={{ color: COLORS.PRIMARY, fontWeight: 600, whiteSpace: "normal" }}>
                + Add Custom Sub-item
              </MenuItem>
              {selectedChildren.length > 0 && (
                <Box sx={{ p: 1, position: 'sticky', bottom: 0, bgcolor: 'white', zIndex: 1, borderTop: '1px solid #eee' }}>
                  <Button fullWidth variant="contained" size="small" onClick={(e) => {
                    e.stopPropagation();
                    onAddChild(node.id, selectedChildren);
                    setSelectedChildren([]);
                    setChildDropdownOpen(false);
                  }}>
                    Add {selectedChildren.length} Selected
                  </Button>
                </Box>
              )}
            </Select>
          </FormControl>
        </Box>

      </Paper>

      {/* Render children with indentation */}
      {node.subItems && node.subItems.length > 0 && isExpanded && (
        <Box
          sx={{
            ml: 4,
            mt: 2,
            borderLeft: `2px solid ${COLORS.LIGHT_SECONDARY || "#E6DAF3"}`,
            pl: 2,
          }}
        >
          {node.subItems.map((sub, index) => (
            <DynamicNodeBuilder
              key={sub.id}
              node={sub}
              isFirst={index === 0}
              isLast={index === node.subItems.length - 1}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDelete={onDelete}
              onDetailChange={onDetailChange}
              onAddSibling={onAddSibling}
              onAddChild={onAddChild}
              onAddCustomSibling={onAddCustomSibling}
              onAddCustomChild={onAddCustomChild}
              onCustomDetailChange={onCustomDetailChange}
              onAddCustomDetail={onAddCustomDetail}
              onRemoveCustomDetail={onRemoveCustomDetail}
              onRemoveMasterDetail={onRemoveMasterDetail}
              onTitleChange={onTitleChange}
              sourceOptions={sourceOptions}
              expandTrigger={expandTrigger}
              collapseTrigger={collapseTrigger}
              onCopy={onCopy}
              level={level + 1}
              parentChildOptions={childOptions}
              masterNodeMap={masterNodeMap}
              onOpenSort={onOpenSort}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

// Intelligently parses titles (e.g., Floor 1 or Parking) and existing sibling titles to generate a sequential list of clone titles
const getSequenceTitles = (sourceTitle, count, siblings) => {
  const match = sourceTitle.match(/^(.*?)\s*(\d+)$/);
  if (match) {
    const prefix = match[1].trim();
    const startNum = parseInt(match[2], 10);

    // Find all sibling numbers with this prefix
    const existingNums = siblings
      .map((s) => {
        const m = s.title.match(
          new RegExp(`^${escapeRegExp(prefix)}\\s*(\\d+)$`, "i")
        );
        return m ? parseInt(m[1], 10) : null;
      })
      .filter((n) => n !== null);

    let nextNum;
    if (existingNums.length > 0) {
      nextNum = Math.max(...existingNums) + 1;
    } else {
      nextNum = startNum;
    }

    const titles = [];
    for (let i = 0; i < count; i++) {
      titles.push(`${prefix} ${nextNum + i}`);
    }
    return titles;
  } else {
    const prefix = sourceTitle.trim();
    const existingNums = siblings
      .map((s) => {
        if (s.title.toLowerCase() === prefix.toLowerCase()) return 0;
        const m = s.title.match(
          new RegExp(`^${escapeRegExp(prefix)}\\s*(\\d+)$`, "i")
        );
        return m ? parseInt(m[1], 10) : null;
      })
      .filter((n) => n !== null);

    let nextNum;
    if (existingNums.length > 0) {
      nextNum = Math.max(...existingNums) + 1;
    } else {
      nextNum = 1;
    }

    const titles = [];
    for (let i = 0; i < count; i++) {
      if (
        i === 0 &&
        !siblings.some(
          (s) => s.title.toLowerCase() === prefix.toLowerCase()
        )
      ) {
        titles.push(prefix);
      } else {
        titles.push(`${prefix} ${nextNum + i}`);
      }
    }
    return titles;
  }
};

const StartProjectMapping = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [masterMappings, setMasterMappings] = useState([]);
  
  const [project, setProject] = useState(null);
  const [allMappings, setAllMappings] = useState([]);
  const [activeTower, setActiveTower] = useState("");
  const [selectedTowersToClone, setSelectedTowersToClone] = useState([]);

  // Selection State
  const [projectType, setProjectType] = useState("");
  const [customProjectType, setCustomProjectType] = useState("");
  const [selectedSourceNames, setSelectedSourceNames] = useState("");
  const [selectedMains, setSelectedMains] = useState([]);

  // The Build State
  const [components, setComponents] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    node: null,
  });
  const [typeChangeConfirm, setTypeChangeConfirm] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const [expandTrigger, setExpandTrigger] = useState(0);
  const [collapseTrigger, setCollapseTrigger] = useState(0);
  const [isAllExpanded, setIsAllExpanded] = useState(true);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startScratchConfirm, setStartScratchConfirm] = useState(false);

  const pushToHistory = useCallback((newComponents) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newComponents);
      return newHistory;
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      startTransition(() => {
        setComponents(history[newIndex]);
      });
    }
  };

  const handleResetAll = () => {
    if (history.length > 0) {
      setHistoryIndex(0);
      startTransition(() => {
        setComponents(history[0]);
      });
    }
  };

  const updateComponents = useCallback((newComponentsOrUpdater) => {
    setComponents((prev) => {
      const newComponents = typeof newComponentsOrUpdater === 'function' ? newComponentsOrUpdater(prev) : newComponentsOrUpdater;
      pushToHistory(newComponents);
      return newComponents;
    });
  }, [pushToHistory]);
  // Dialog state for cloning
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [nodeToClone, setNodeToClone] = useState(null);
  const [cloneTargetParentId, setCloneTargetParentId] = useState("root");
  const [targetAnchorEl, setTargetAnchorEl] = useState(null);
  const [cloneCount, setCloneCount] = useState(1);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneMethod, setCloneMethod] = useState("create");
  const [selectedExistingTargets, setSelectedExistingTargets] = useState([]);
  const [generatedNames, setGeneratedNames] = useState([]);
  const [cloneTargetTower, setCloneTargetTower] = useState("");
  const [targetTowerComponents, setTargetTowerComponents] = useState([]);

  // Dialog state for custom component name
  const [customNodeDialog, setCustomNodeDialog] = useState({
    open: false,
    mode: "", // "main", "sibling", "child"
    targetId: null,
    title: "",
  });

  const [sortDialogState, setSortDialogState] = useState({
    open: false,
    items: [],
    parentId: null,
  });

  const DRAFT_KEY = `project_mapping_draft_${projectId || "new"}_${activeTower || "default"}`;

  // Save to draft whenever state changes, using debounce to prevent freezing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (projectType || selectedSourceNames || components.length > 0) {
        const draftData = {
          projectType,
          customProjectType,
          selectedSourceNames,
          components,
          timestamp: new Date().getTime(),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [projectType, customProjectType, selectedSourceNames, components, DRAFT_KEY]);

  useEffect(() => {
    if (cloneDialogOpen && nodeToClone && cloneMethod === "create") {
      let siblings = [];
      if (cloneTargetParentId === "root") {
        siblings = components;
      } else {
        const findSiblings = (items) => {
          for (const item of items) {
            if (item.id === cloneTargetParentId) {
              siblings = item.subItems || [];
              return;
            }
            findSiblings(item.subItems || []);
          }
        };
        findSiblings(components);
      }
      const titles = getSequenceTitles(nodeToClone.title, cloneCount, siblings);
      setGeneratedNames(titles);
    }
  }, [cloneDialogOpen, nodeToClone, cloneCount, cloneTargetParentId, cloneMethod, components]);

  useEffect(() => {
    if (cloneDialogOpen && cloneTargetTower) {
      if (cloneTargetTower === activeTower) {
        setTargetTowerComponents(components);
      } else {
        const draftKey = `project_mapping_draft_${projectId || "new"}_${cloneTargetTower}`;
        const savedDraft = localStorage.getItem(draftKey);
        if (savedDraft) {
          try {
            const draft = JSON.parse(savedDraft);
            setTargetTowerComponents(draft.components || []);
            setCloneTargetParentId("root");
            return;
          } catch (e) {}
        }
        
        const mapping = allMappings.find(m => m.towerName === cloneTargetTower);
        if (mapping) {
          setTargetTowerComponents(mapping.components || []);
          setCloneTargetParentId("root");
          return;
        }
        
        setTargetTowerComponents([]);
        setCloneTargetParentId("root");
      }
    }
  }, [cloneTargetTower, activeTower, components, cloneDialogOpen, projectId, allMappings]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      
      let projData = null;
      let currentActiveTower = "";
      if (projectId) {
        try {
          const projRes = await axios.get(`${url}/api/projects/get/${projectId}`, {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          });
          projData = projRes.data.data;
          setProject(projData);
          if (projData && projData.towers && projData.towers.length > 0) {
            currentActiveTower = projData.towers[0].name;
            setActiveTower(currentActiveTower);
          }
        } catch (e) {
          console.error("Error fetching project:", e);
        }
      }

      const masters = await fetchMasterMappings(true, projData);

      // Check for draft first
      const savedDraft = localStorage.getItem(`project_mapping_draft_${projectId || "new"}_${currentActiveTower || "default"}`);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          if (draft.components && draft.components.length > 0) {
            setProjectType(draft.projectType || "");
            if (draft.customProjectType) setCustomProjectType(draft.customProjectType);
            setSelectedSourceNames(draft.selectedSourceNames || "");
            startTransition(() => {
              setComponents(draft.components);
              setHistory([draft.components]);
              setHistoryIndex(0);
            });
            setIsDraftLoaded(true);
            toast.success("Resumed from unsaved draft", { icon: "📝" });
            
            // Still fetch all mappings to keep state updated for other towers
            if (projectId) {
              await fetchExistingMapping(masters, currentActiveTower, projData, true);
            }
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error("Error loading draft:", e);
        }
      }

      if (projectId) {
        await fetchExistingMapping(masters, currentActiveTower, projData, false);
      }
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleTowerChange = (event, newValue) => {
    if (loading) return; // Prevent tab changes while cloning/loading
    setActiveTower(newValue);
    setIsDraftLoaded(false);

    // Check for draft first
    const savedDraft = localStorage.getItem(`project_mapping_draft_${projectId || "new"}_${newValue}`);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        if (draft.components && draft.components.length > 0) {
          setProjectType(draft.projectType || "");
          if (draft.customProjectType) setCustomProjectType(draft.customProjectType);
          setSelectedSourceNames(draft.selectedSourceNames || "");
          startTransition(() => {
            setComponents(draft.components);
            setHistory([draft.components]);
            setHistoryIndex(0);
          });
          setIsDraftLoaded(true);
          toast.success(`Resumed draft for tower ${newValue}`, { icon: "📝" });
          return;
        }
      } catch (e) {
        console.error("Error loading draft:", e);
      }
    }

    setLoading(true);
    setTimeout(() => {
      loadMappingForTower(newValue, allMappings, masterMappings, project);
      setLoading(false);
    }, 50);
  };

  const loadMappingForTower = (towerName, mappings, masters, projData) => {
     let mapping = mappings.find(m => (m.towerName || "") === (towerName || ""));
     if (!mapping && !towerName && mappings.length > 0) {
        mapping = mappings.find(m => !m.towerName) || mappings[0];
     }
     
     if (mapping) {
        const storedType = mapping.projectType || "";
        const formattedType = storedType ? storedType.trim().charAt(0).toUpperCase() + storedType.trim().slice(1) : "";
        
        const matchPType = masters.find((m) => m.mappingName.toLowerCase() === storedType.toLowerCase());
        if (matchPType) {
           const properCaseName = matchPType.mappingName.trim().charAt(0).toUpperCase() + matchPType.mappingName.trim().slice(1);
           setProjectType(properCaseName);
           setCustomProjectType("");
        } else if (formattedType) {
           setProjectType("Custom");
           setCustomProjectType(formattedType);
        } else {
           setProjectType("");
           setCustomProjectType("");
        }

        const types = mapping.sourceTemplates && mapping.sourceTemplates.length > 0
            ? mapping.sourceTemplates
            : mapping.projectType
              ? mapping.projectType.split("+").map((t) => t.trim())
              : [];
        let resolvedTemplateName = "";
        if (types.length > 0) {
          const match = masters.find((m) => m.mappingName.toLowerCase() === types[0].toLowerCase());
          resolvedTemplateName = match ? match.mappingName : types[0];
        } else if (mapping.projectType && masters.length > 0) {
          const match = masters.find((m) => m.mappingName.toLowerCase() === mapping.projectType.toLowerCase());
          if (match) resolvedTemplateName = match.mappingName;
        }
        
        if (resolvedTemplateName && masters.some((m) => m.mappingName.toLowerCase() === resolvedTemplateName.toLowerCase())) {
          setSelectedSourceNames(resolvedTemplateName);
        } else if (mapping.components && mapping.components.length > 0) {
          setSelectedSourceNames("Custom Mapping");
        } else {
          setSelectedSourceNames("");
        }

        const sources = masters.filter((m) => m.mappingName.toLowerCase() === resolvedTemplateName.toLowerCase());
        const newComp = convertToUIState(mapping.components || [], sources, true);
        startTransition(() => {
          setComponents(newComp);
          setHistory([newComp]);
          setHistoryIndex(0);
        });
     } else {
        // No existing mapping. Check if tower has projectType
        let pType = "";
        let formattedPType = "";
        
        if (projData && projData.propertyType) {
            pType = projData.propertyType;
            formattedPType = pType;
        } else if (towerName && projData && projData.towers) {
           const tDef = projData.towers.find(t => t.name === towerName);
           if (tDef) {
             if (tDef.projectType === "Custom Mapping" && tDef.customTypeName) {
                 pType = "Custom";
                 formattedPType = tDef.customTypeName;
             } else {
                 pType = tDef.projectType;
                 formattedPType = pType ? pType.trim().charAt(0).toUpperCase() + pType.trim().slice(1) : "";
             }
           }
        }
        
        const matchPType = pType ? masters.find((m) => m.mappingName.toLowerCase() === pType.toLowerCase()) : null;
        
        if (matchPType) {
           const properCaseName = matchPType.mappingName.trim().charAt(0).toUpperCase() + matchPType.mappingName.trim().slice(1);
           setProjectType(properCaseName);
           setCustomProjectType("");
        } else if (pType) {
           setProjectType("Custom");
           setCustomProjectType(formattedPType);
        } else {
           setProjectType("");
           setCustomProjectType("");
        }

        if (pType) {
           const match = masters.find((m) => m.mappingName.toLowerCase() === pType.toLowerCase());
           if (match) {
             setSelectedSourceNames(match.mappingName);
             const newComp = convertToUIState(match.components, [match], false);
             startTransition(() => {
               setComponents(newComp);
               setHistory([newComp]);
               setHistoryIndex(0);
             });
           } else {
             setSelectedSourceNames("Custom Mapping");
             startTransition(() => {
               setComponents([]);
               setHistory([[]]);
               setHistoryIndex(0);
             });
           }
        } else {
           setSelectedSourceNames("");
           startTransition(() => {
             setComponents([]);
             setHistory([[]]);
             setHistoryIndex(0);
           });
        }
     }
  };

  const fetchMasterMappings = async (skipLoad = false, projData = null) => {
    if (!skipLoad) setLoading(true);
    try {
      let endpoint = `${url}/api/master-mappings/all`;
      if (projData?.source === "FIR") {
        endpoint = `${url}/api/fir/mappings`;
      }
      const response = await axios.get(endpoint, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      if (response.data.success) {
        // Adapt FirMappings to match MasterMappings shape if necessary
        const mappings = response.data.data.map(m => {
          if (projData?.source === "FIR") {
            return {
              _id: m._id,
              mappingName: m.propertyType,
              components: m.components.map(c => ({ 
                ...c, 
                title: typeof c === 'string' ? c : c.title,
                extraDetails: c.extraDetails || []
              }))
            };
          }
          return m;
        });
        setMasterMappings(mappings);
        return mappings;
      }
    } catch (error) {
      console.error("Error fetching mappings:", error);
    } finally {
      if (!skipLoad) setLoading(false);
    }
    return [];
  };

  const fetchExistingMapping = async (masters, currentTower, projData, skipLoad = false) => {
    try {
      if (!skipLoad) setLoading(true);
      const res = await axios.get(
        `${url}/api/project-mappings/get/${projectId}`,
        {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        },
      );

      if (res.data?.success && res.data.data) {
        setAllMappings(res.data.data);
        if (!skipLoad) {
          loadMappingForTower(currentTower, res.data.data, masters, projData);
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error("Error fetching project mapping:", err);
    } finally {
      if (!skipLoad) setLoading(false);
    }
  };

  const handleCloneToOtherTower = async () => {
    if (selectedTowersToClone.length === 0) return;
    try {
      setLoading(true);
      const cleanTree = (items) =>
        items.map((item) => {
          const baseDetails = (item.masterExtraDetails || []).map((key, idx) => ({
            key,
            value: item.extraDetails[idx] || "",
          }));
          const customDetails = (item.customExtraDetails || []).filter(d => d.key.trim() !== "");
          return {
            title: item.title,
            extraDetails: [...baseDetails, ...customDetails],
            subItems: cleanTree(item.subItems),
          };
        });

      // Clone to all selected towers in parallel PLUS the current active tower
      const towersToSave = [...new Set([...selectedTowersToClone, activeTower])];

      await Promise.all(
        towersToSave.map(async (targetTowerName) => {
          await axios.post(
            `${url}/api/project-mappings/add/${projectId}`,
            {
              projectType: projectType === "Custom" ? customProjectType : projectType, 
              towerName: targetTowerName,
              sourceTemplates: selectedSourceNames === "Custom Mapping" ? [] : (selectedSourceNames ? [selectedSourceNames] : []),
              components: cleanTree(components),
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          localStorage.removeItem(`project_mapping_draft_${projectId || "new"}_${targetTowerName}`);
        })
      );

      // Update allMappings state locally to prevent race conditions when user switches tabs quickly
      setAllMappings((prev) => {
        const newMappings = [...prev];
        towersToSave.forEach((tName) => {
          const idx = newMappings.findIndex((m) => m.towerName === tName);
          const updatedMapping = {
            projectId,
            towerName: tName,
            projectType: projectType === "Custom" ? customProjectType : projectType,
            sourceTemplates: selectedSourceNames === "Custom Mapping" ? [] : (selectedSourceNames ? [selectedSourceNames] : []),
            components: JSON.parse(JSON.stringify(components)), // deep copy to prevent reference issues
          };
          if (idx > -1) {
            newMappings[idx] = updatedMapping;
          } else {
            newMappings.push(updatedMapping);
          }
        });
        return newMappings;
      });

      // Also trigger a background fetch just in case
      fetchExistingMapping(masterMappings, activeTower, project, true);

      toast.success(`Successfully cloned mapping to ${selectedTowersToClone.length} tower(s)!`);
      setSelectedTowersToClone([]);

    } catch (e) {
      toast.error(`Failed to clone mapping.`);
    } finally {
      setLoading(false);
    }
  };

  const convertToUIState = (backendItems, sources, isExisting = false) => {
    const masterMap = new Map();
    const buildMap = (nodes) => {
      for (const n of nodes) {
        const key = n.title ? n.title.trim().toLowerCase() : "";
        if (!masterMap.has(key)) {
           masterMap.set(key, n);
        }
        if (n.subItems) {
           buildMap(n.subItems);
        }
      }
    };
    sources.forEach((s) => {
      if (s.components) {
        buildMap(s.components);
      }
    });

    const traverseAndConvert = (items) => {
      return items.map((item) => {
        const key = item.title ? item.title.trim().toLowerCase() : "";
        const masterNode = masterMap.get(key) || null;

        const savedDetails = item.extraDetails || [];
        const masterTemplateKeys = (masterNode?.extraDetails || []).map((ed) =>
          typeof ed === "object" ? ed.key : ed,
        );

        // If we have saved details, we need to categorize them
        const masterKeys = [];
        const masterValues = [];
        const masterPlaceholders = [];
        const customDetails = [];

        if (isExisting && savedDetails.length > 0) {
          savedDetails.forEach((sd) => {
            const key = typeof sd === "object" ? sd.key : "detail";
            const value = typeof sd === "object" ? sd.value : sd;

            masterKeys.push(key);
            masterValues.push(value);
            masterPlaceholders.push("");
          });
        } else {
          // Fallback for first-time population from template
          masterTemplateKeys.forEach((key) => {
            masterKeys.push(key);
            masterValues.push("");
            // Find the default value from the template to use as placeholder
            const templateDetail = (masterNode?.extraDetails || []).find(ed => (typeof ed === 'object' ? ed.key : ed) === key);
            masterPlaceholders.push(typeof templateDetail === 'object' ? templateDetail.value : "");
          });
        }

        return {
          id: Math.random().toString(36).substr(2, 9),
          masterId: masterNode?._id,
          title: item.title,
          masterExtraDetails: masterKeys,
          extraDetails: masterValues,
          placeholderDetails: masterPlaceholders,
          customExtraDetails: customDetails,
          subItems: item.subItems ? traverseAndConvert(item.subItems) : [],
        };
      });
    };

    return traverseAndConvert(backendItems);
  };

  const selectedSources = masterMappings.filter(
    (m) => m.mappingName.toLowerCase() === selectedSourceNames.toLowerCase(),
  );

  const masterNodeMap = React.useMemo(() => {
    const map = new Map();
    const buildMap = (nodes) => {
      for (const n of nodes) {
        map.set(String(n._id), n);
        if (n.subItems) buildMap(n.subItems);
      }
    };
    selectedSources.forEach(s => {
       if (s.components) buildMap(s.components);
    });
    return map;
  }, [selectedSources]);

  const generateNodeObj = (masterNode) => ({
    id: Math.random().toString(36).substr(2, 9), // Local unique ID for UI
    masterId: masterNode._id,
    title: masterNode.title,
    masterExtraDetails: (masterNode.extraDetails || []).map((ed) =>
      typeof ed === "object" ? ed.key : ed,
    ),
    extraDetails: (masterNode.extraDetails || []).map(() => ""),
    placeholderDetails: (masterNode.extraDetails || []).map((ed) => 
      typeof ed === "object" ? ed.value : ""
    ),
    customExtraDetails: [],
    subItems: masterNode.subItems ? masterNode.subItems.map(generateNodeObj) : [],
  });

  const generateCustomNodeObj = () => ({
    id: Math.random().toString(36).substr(2, 9),
    masterId: null,
    title: "New Component",
    masterExtraDetails: [],
    extraDetails: [],
    customExtraDetails: [],
    subItems: [],
  });

  const handleAddComponent = (masterNode) => {
    updateComponents((prev) => {
      if (Array.isArray(masterNode)) {
        return [...prev, ...masterNode.map((mn) => generateNodeObj(mn))];
      }
      return [...prev, generateNodeObj(masterNode)];
    });
  };

  const handleAddCustomMain = () => {
    setCustomNodeDialog({
      open: true,
      mode: "main",
      targetId: null,
      title: "",
    });
  };

  const handleCustomDialogSubmit = () => {
    const title = customNodeDialog.title.trim() || "New Component";
    if (customNodeDialog.mode === "main") {
      updateComponents((prev) => [...prev, { ...generateCustomNodeObj(), title }]);
    } else if (customNodeDialog.mode === "sibling") {
      handleAddSibling(customNodeDialog.targetId, "custom", title);
    } else if (customNodeDialog.mode === "child") {
      handleAddChild(customNodeDialog.targetId, "custom", title);
    }
    setCustomNodeDialog({ open: false, mode: "", targetId: null, title: "" });
  };

  const handleAddSibling = (targetId, masterNode, customTitle = "New Component") => {
    const addRecursively = (items) => {
      let index = items.findIndex((item) => item.id === targetId);
      if (index !== -1) {
        const newItems = [...items];
        if (Array.isArray(masterNode)) {
          const newNodes = masterNode.map((mn) => generateNodeObj(mn));
          newItems.splice(index + 1, 0, ...newNodes);
        } else {
          const newNode = masterNode === "custom" ? { ...generateCustomNodeObj(), title: customTitle } : generateNodeObj(masterNode);
          newItems.splice(index + 1, 0, newNode);
        }
        return newItems;
      }
      return items.map((item) => ({
        ...item,
        subItems: addRecursively(item.subItems),
      }));
    };
    updateComponents((prev) => addRecursively(prev));
  };

  const handleAddChild = (parentId, masterNode, customTitle = "New Component") => {
    const addRecursively = (items) => {
      return items.map((item) => {
        if (item.id === parentId) {
          let newNodes = [];
          if (Array.isArray(masterNode)) {
            newNodes = masterNode.map((mn) => generateNodeObj(mn));
          } else {
            newNodes = [masterNode === "custom" ? { ...generateCustomNodeObj(), title: customTitle } : generateNodeObj(masterNode)];
          }
          return {
            ...item,
            subItems: [...item.subItems, ...newNodes],
          };
        }
        return { ...item, subItems: addRecursively(item.subItems) };
      });
    };
    updateComponents((prev) => addRecursively(prev));
  };

  const handleCustomDetailChange = (id, idx, field, value) => {
    const updateRecursively = (items) => {
      return items.map((item) => {
        if (item.id === id) {
          const newList = [...(item.customExtraDetails || [])];
          newList[idx] = { ...newList[idx], [field]: value };
          return { ...item, customExtraDetails: newList };
        }
        return { ...item, subItems: updateRecursively(item.subItems) };
      });
    };
    updateComponents((prev) => updateRecursively(prev));
  };

  const handleAddCustomDetail = (id) => {
    const updateRecursively = (items) => {
      return items.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            customExtraDetails: [...(item.customExtraDetails || []), { key: "", value: "" }]
          };
        }
        return { ...item, subItems: updateRecursively(item.subItems) };
      });
    };
    updateComponents((prev) => updateRecursively(prev));
  };

  const handleRemoveCustomDetail = (id, idx) => {
    const updateRecursively = (items) => {
      return items.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            customExtraDetails: item.customExtraDetails.filter((_, i) => i !== idx)
          };
        }
        return { ...item, subItems: updateRecursively(item.subItems) };
      });
    };
    updateComponents((prev) => updateRecursively(prev));
  };

  const handleTitleChange = (id, value) => {
    const updateRecursively = (items) => {
      return items.map((item) => {
        if (item.id === id) {
          return { ...item, title: value };
        }
        return { ...item, subItems: updateRecursively(item.subItems) };
      });
    };
    updateComponents((prev) => updateRecursively(prev));
  };

  const handleDetailChange = (id, detailIdx, value) => {
    const updateRecursively = (items) => {
      return items.map((item) => {
        if (item.id === id) {
          const newDetails = [...item.extraDetails];
          newDetails[detailIdx] = value;
          
          let updatedItem = { ...item, extraDetails: newDetails };

          const titleLower = item.title?.toLowerCase() || "";
          if (titleLower === "floors" || titleLower === "tower" || titleLower === "building") {
            const masterKeyLower = item.masterExtraDetails[detailIdx]?.toLowerCase();
            
            if (masterKeyLower === "total floors" || masterKeyLower === "no of floors" || masterKeyLower === "number of floors") {
              const numFloors = parseInt(value, 10);
              if (!isNaN(numFloors) && numFloors >= 0) {
                let currentSubItems = item.subItems ? [...item.subItems] : [];
                const existingFloors = currentSubItems.filter(sub => sub.title?.toLowerCase().startsWith("floor "));
                const nonFloors = currentSubItems.filter(sub => !sub.title?.toLowerCase().startsWith("floor "));
                
                let updatedFloors = [...existingFloors];
                
                if (numFloors > existingFloors.length) {
                   let floorMaster = null;
                   const searchMaster = (nodes) => {
                      for (const n of nodes) {
                         if (n._id === item.masterId) {
                            if (n.subItems) {
                               floorMaster = n.subItems.find(s => s.title.toLowerCase() === "typical residential floor")
                                          || n.subItems.find(s => s.title.toLowerCase().includes("floor"))
                                          || n.subItems[0];
                            }
                            return true;
                         }
                         if (n.subItems && searchMaster(n.subItems)) return true;
                      }
                      return false;
                   };
                   selectedSources.forEach(s => searchMaster(s.components));

                   for (let i = existingFloors.length + 1; i <= numFloors; i++) {
                      const newNode = floorMaster ? generateNodeObj(floorMaster) : generateCustomNodeObj();
                      newNode.title = `Floor ${i}`;
                      updatedFloors.push(newNode);
                   }
                } else if (numFloors < existingFloors.length) {
                   updatedFloors = updatedFloors.slice(0, numFloors);
                }
                
                updatedItem.subItems = [...nonFloors, ...updatedFloors];
              }
            }
          }
          
          return updatedItem;
        }
        return { ...item, subItems: updateRecursively(item.subItems) };
      });
    };
    updateComponents((prev) => updateRecursively(prev));
  };

  const handleRemoveMasterDetail = (id, detailIdx) => {
    const updateRecursively = (items) => {
      return items.map((item) => {
        if (item.id === id) {
          const newDetails = [...item.extraDetails];
          const newKeys = [...item.masterExtraDetails];
          newDetails.splice(detailIdx, 1);
          newKeys.splice(detailIdx, 1);
          return { ...item, extraDetails: newDetails, masterExtraDetails: newKeys };
        }
        return { ...item, subItems: updateRecursively(item.subItems) };
      });
    };
    updateComponents((prev) => updateRecursively(prev));
  };

  const handleDeleteNode = (node) => {
    setDeleteConfirm({ open: true, node });
  };

  const executeDeleteNode = (id) => {
    const deleteRecursively = (items) => {
      return items
        .filter((item) => item.id !== id)
        .map((item) => ({
          ...item,
          subItems: deleteRecursively(item.subItems),
        }));
    };
    updateComponents((prev) => deleteRecursively(prev));
  };

  const handleMoveUp = (id) => {
    const moveRecursively = (items) => {
      const idx = items.findIndex((item) => item.id === id);
      if (idx > 0) {
        const newItems = [...items];
        const temp = newItems[idx];
        newItems[idx] = newItems[idx - 1];
        newItems[idx - 1] = temp;
        return newItems;
      }
      return items.map((item) => ({
        ...item,
        subItems: moveRecursively(item.subItems || []),
      }));
    };
    updateComponents((prev) => moveRecursively(prev));
  };

  const handleMoveDown = (id) => {
    const moveRecursively = (items) => {
      const idx = items.findIndex((item) => item.id === id);
      if (idx !== -1 && idx < items.length - 1) {
        const newItems = [...items];
        const temp = newItems[idx];
        newItems[idx] = newItems[idx + 1];
        newItems[idx + 1] = temp;
        return newItems;
      }
      return items.map((item) => ({
        ...item,
        subItems: moveRecursively(item.subItems || []),
      }));
    };
    updateComponents((prev) => moveRecursively(prev));
  };

  const findNodeById = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.subItems && node.subItems.length > 0) {
        const found = findNodeById(node.subItems, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleOpenSort = (nodeId) => {
    let parentId = findParentId(components, nodeId) || "root";
    let itemsToDrag = [];
    if (parentId === "root") {
      itemsToDrag = components;
    } else {
      const parentNode = findNodeById(components, parentId);
      if (parentNode) {
        itemsToDrag = parentNode.subItems || [];
      }
    }
    setSortDialogState({ open: true, items: itemsToDrag, parentId });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSortDialogState((prev) => {
        const oldIndex = prev.items.findIndex((item) => item.id === active.id);
        const newIndex = prev.items.findIndex((item) => item.id === over.id);
        return {
          ...prev,
          items: arrayMove(prev.items, oldIndex, newIndex),
        };
      });
    }
  };

  const handleApplySort = () => {
    const { items, parentId } = sortDialogState;
    if (parentId === "root") {
      updateComponents(items);
    } else {
      const updateRecursively = (nodes) => {
        return nodes.map((node) => {
          if (node.id === parentId) {
             return { ...node, subItems: items };
          }
          return { ...node, subItems: updateRecursively(node.subItems || []) };
        });
      };
      updateComponents((prev) => updateRecursively(prev));
    }
    setSortDialogState({ open: false, items: [], parentId: null });
  };

  // Traverses the component tree to find the parent ID of the node to clone, enabling us to default the target path to the current parent.
  const findParentId = (nodes, targetId, currentParentId = "root") => {
    for (const node of nodes) {
      if (node.id === targetId) {
        return currentParentId;
      }
      if (node.subItems && node.subItems.length > 0) {
        const found = findParentId(node.subItems, targetId, node.id);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };

  // Returns all descendant IDs of a node to prevent users from cloning a node inside itself or its children.
  const getDescendantIds = (node) => {
    if (!node) return [];
    let ids = [node.id];
    if (node.subItems) {
      node.subItems.forEach((child) => {
        ids = [...ids, ...getDescendantIds(child)];
      });
    }
    return ids;
  };

  // Generates a flat list of all component paths in the tree (e.g., Floors > Floor 1) to populate the destination dropdown.
  const buildPathOptions = (nodes, currentPath = "") => {
    let options = [];
    nodes.forEach((node) => {
      const nodePath = currentPath ? `${currentPath} > ${node.title}` : node.title;
      options.push({
        id: node.id,
        title: node.title,
        path: nodePath,
      });
      if (node.subItems && node.subItems.length > 0) {
        options = [...options, ...buildPathOptions(node.subItems, nodePath)];
      }
    });
    return options;
  };

  const handleCopyProjectNode = (node) => {
    const parentId = findParentId(components, node.id) || "root";
    setNodeToClone(node);
    setCloneTargetParentId(parentId);
    setCloneCount(1);
    setCloneMethod("create");
    setSelectedExistingTargets([]);
    setCloneTargetTower(activeTower);
    setTargetTowerComponents(components);
    setCloneDialogOpen(true);
  };

  const executeClone = () => {
    if (!nodeToClone) return;
    setIsCloning(true);

    setTimeout(() => {
      // --- Intelligent Cloner Helper ---
      const getIntelligentCloner = (originalNode, targetTitle) => {
        let oldNumMatch = originalNode.title.match(/Floor\s*(\d+)/i);
        let oldNum = oldNumMatch ? parseInt(oldNumMatch[1], 10) : null;
        
        let newNumMatch = targetTitle.match(/Floor\s*(\d+)/i);
        let newNum = newNumMatch ? parseInt(newNumMatch[1], 10) : null;

        let usedOldPrefix = "";
        let usedNewPrefix = "";
        
        if (oldNum !== null && newNum !== null) {
          let oldPrefix0 = String(oldNum - 1);
          let newPrefix0 = String(newNum - 1);
          let oldPrefix1 = String(oldNum);
          let newPrefix1 = String(newNum);

          let count0 = 0;
          let count1 = 0;
          if (originalNode.subItems && originalNode.subItems.length > 0) {
              originalNode.subItems.forEach(child => {
                  let title = child.title.trim();
                  let match = title.match(/((?:\d+)(?:[\s/,&-]+(?:\d+))*)$/);
                  if (match) {
                      let numbersStr = match[1];
                      let numbers = numbersStr.match(/\d+/g) || [];
                      numbers.forEach(num => {
                          if (oldPrefix0 && num.startsWith(oldPrefix0) && num.length > oldPrefix0.length) count0++;
                          if (oldPrefix1 && num.startsWith(oldPrefix1) && num.length > oldPrefix1.length) count1++;
                      });
                  }
              });
          }

          if (count1 > 0 && count1 > count0) {
              usedOldPrefix = oldPrefix1;
              usedNewPrefix = newPrefix1;
          } else {
              usedOldPrefix = oldPrefix0;
              usedNewPrefix = newPrefix0;
          }
        }

        const cloner = (node, isDirectChild = false) => {
          let clonedTitle = node.title;

          if (isDirectChild && oldNum !== null && newNum !== null) {
              let titleStr = clonedTitle.trim();
              let match = titleStr.match(/^(.*?)((?:\d+)(?:[\s/,&-]+(?:\d+))*)$/);
              
              if (match) {
                  let prefixStr = match[1];
                  let numbersStr = match[2];

                  let newNumbersStr = numbersStr.replace(/\d+/g, (numStr) => {
                      if (usedOldPrefix && numStr.startsWith(usedOldPrefix)) {
                          let flatNum = numStr.substring(usedOldPrefix.length);
                          return usedNewPrefix + flatNum;
                      } else {
                          return usedNewPrefix + numStr;
                      }
                  });

                  clonedTitle = prefixStr + newNumbersStr;
              }
          }

          return {
            ...node,
            title: clonedTitle,
            id: Math.random().toString(36).substr(2, 9),
            extraDetails: [...(node.extraDetails || [])],
            masterExtraDetails: [...(node.masterExtraDetails || [])],
            customExtraDetails: (node.customExtraDetails || []).map(d => ({ ...d })),
            subItems: (node.subItems || []).map(child => cloner(child, false)),
          };
        };

        return cloner;
      };
      // ---------------------------------

      if (cloneMethod === "existing") {
        if (selectedExistingTargets.length === 0) {
          toast.error("Please select at least one component to clone into.");
          setIsCloning(false);
          return;
        }

        const applyToExisting = (items) => {
          return items.map((item) => {
            if (selectedExistingTargets.includes(item.id)) {
               const intelligentCloner = getIntelligentCloner(nodeToClone, item.title);
               const duplicatedData = {
                 ...nodeToClone,
                 id: Math.random().toString(36).substr(2, 9),
                 subItems: (nodeToClone.subItems || []).map(child => intelligentCloner(child, true)),
               };

               return {
                 ...item,
                 extraDetails: [...(duplicatedData.extraDetails || [])],
                 customExtraDetails: (duplicatedData.customExtraDetails || []).map(d => ({ ...d })),
                 subItems: [...(duplicatedData.subItems || [])],
                 masterExtraDetails: [...(duplicatedData.masterExtraDetails || [])],
                 masterId: duplicatedData.masterId || item.masterId,
               };
            }
            return { ...item, subItems: applyToExisting(item.subItems || []) };
          });
        };

        const newTargetComponents = applyToExisting(targetTowerComponents);
        
        if (cloneTargetTower === activeTower) {
          updateComponents(newTargetComponents);
          toast.success(`Successfully applied data to ${selectedExistingTargets.length} component(s)!`);
        } else {
          saveToTargetTowerDraft(newTargetComponents);
          toast.success(`Applied data to ${selectedExistingTargets.length} component(s) in ${cloneTargetTower}'s draft.`, { duration: 5000 });
        }

      } else {
        const titles = generatedNames;

        const newClonedNodes = titles.map((title) => {
            const intelligentCloner = getIntelligentCloner(nodeToClone, title);
            return {
                ...nodeToClone,
                id: Math.random().toString(36).substr(2, 9),
                title: title,
                subItems: (nodeToClone.subItems || []).map(child => intelligentCloner(child, true))
            };
        });

        let newTargetComponents;
        if (cloneTargetParentId === "root") {
          newTargetComponents = [...targetTowerComponents, ...newClonedNodes];
        } else {
          const addRecursively = (items) => {
            return items.map((item) => {
              if (item.id === cloneTargetParentId) {
                return {
                  ...item,
                  subItems: [...item.subItems, ...newClonedNodes],
                };
              }
              return { ...item, subItems: addRecursively(item.subItems) };
            });
          };
          newTargetComponents = addRecursively(targetTowerComponents);
        }

        if (cloneTargetTower === activeTower) {
          updateComponents(newTargetComponents);
          toast.success(`Successfully cloned "${nodeToClone.title}" ${cloneCount} time(s)!`);
        } else {
          saveToTargetTowerDraft(newTargetComponents);
          toast.success(`Cloned "${nodeToClone.title}" to ${cloneTargetTower}'s draft. Switch to ${cloneTargetTower} and save mapping.`, { duration: 6000 });
        }
      }
      
      setIsCloning(false);
      setCloneDialogOpen(false);
      setNodeToClone(null);
    }, 600);
  };

  const saveToTargetTowerDraft = (newComponents) => {
    const targetDraftKey = `project_mapping_draft_${projectId || "new"}_${cloneTargetTower}`;
    const targetDraftStr = localStorage.getItem(targetDraftKey);
    let targetDraft = {
      projectType: "",
      customProjectType: "",
      selectedSourceNames: "",
      components: [],
    };
    if (targetDraftStr) {
      try { targetDraft = JSON.parse(targetDraftStr); } catch(e) {}
    } else {
      const tDef = project?.towers?.find(t => t.name === cloneTargetTower);
      targetDraft.projectType = tDef ? (tDef.projectType === "Custom Mapping" ? "Custom" : tDef.projectType) : "";
      targetDraft.customProjectType = tDef?.customTypeName || "";
      const mapping = allMappings.find(m => m.towerName === cloneTargetTower);
      targetDraft.selectedSourceNames = mapping ? mapping.sourceTemplates?.[0] : "";
    }
    
    targetDraft.components = newComponents;
    targetDraft.timestamp = new Date().getTime();
    localStorage.setItem(targetDraftKey, JSON.stringify(targetDraft));
  };

  const handleSubmit = async () => {
    if (!projectType || components.length === 0) {
      toast.error("Please set project type and add at least one component");
      return;
    }

    try {
      setLoading(true);
      // Backend expects components: [ { title, extraDetails, subItems: [...] } ]
      const cleanTree = (items) =>
        items.map((item) => {
          const baseDetails = (item.masterExtraDetails || []).map((key, idx) => ({
            key,
            value: item.extraDetails[idx] || "",
          }));
          const customDetails = (item.customExtraDetails || []).filter(d => d.key.trim() !== "");
          
          return {
            title: item.title,
            extraDetails: [...baseDetails, ...customDetails],
            subItems: cleanTree(item.subItems),
          };
        });

      // Save the current active tower
      await axios.post(
        `${url}/api/project-mappings/add/${projectId}`,
        {
          projectType: projectType === "Custom" ? customProjectType : projectType,
          rawProjectType: projectType === "Custom" ? "Custom Mapping" : projectType,
          customTypeName: projectType === "Custom" ? customProjectType : "",
          towerName: activeTower,
          sourceTemplates: selectedSourceNames === "Custom Mapping" ? [] : (selectedSourceNames ? [selectedSourceNames] : []),
          components: cleanTree(components),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      localStorage.removeItem(DRAFT_KEY);

      // Save any other towers that have drafts for this project
      if (project && project.towers) {
        for (const tower of project.towers) {
          if (tower.name === activeTower) continue;
          
          const draftKey = `project_mapping_draft_${projectId || "new"}_${tower.name}`;
          const savedDraft = localStorage.getItem(draftKey);
          if (savedDraft) {
             try {
                const draft = JSON.parse(savedDraft);
                if (draft.components && draft.components.length > 0) {
                   await axios.post(
                     `${url}/api/project-mappings/add/${projectId}`,
                     {
                       projectType: draft.projectType === "Custom" ? draft.customProjectType : draft.projectType,
                       rawProjectType: draft.projectType === "Custom" ? "Custom Mapping" : draft.projectType,
                       customTypeName: draft.projectType === "Custom" ? draft.customProjectType : "",
                       towerName: tower.name,
                       sourceTemplates: draft.selectedSourceNames === "Custom Mapping" ? [] : (draft.selectedSourceNames ? [draft.selectedSourceNames] : []),
                       components: cleanTree(draft.components),
                     },
                     {
                       headers: { Authorization: `Bearer ${token}` },
                     },
                   );
                   localStorage.removeItem(draftKey);
                }
             } catch(e) {
                console.error("Error saving draft for tower:", tower.name, e);
             }
          }
        }
      }
      toast.success("Project mapping saved successfully");
      setTimeout(() => {
        setLoading(false);
        if (project?.source === "FIR") {
          navigate("/projects/fir-requests");
        } else {
          navigate("/technical/projects/in-progress");
        }
      }, 1500);
    } catch (error) {
      console.error("Error saving mapping:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to save mapping";
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const projectTypes = Array.from(new Set(
    masterMappings.map(m => m.mappingName.trim().charAt(0).toUpperCase() + m.mappingName.trim().slice(1))
  ));


  return (
    <Box sx={{ padding: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ color: COLORS.SECONDARY, mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            ...TYPOGRAPHY.H3,
            color: COLORS.SECONDARY,
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
          }}
        >
          {projectId ? "Edit Project Mapping" : "Initialize Project Mapping"}
        </Typography>
      </Box>

      {project && project.towers && project.towers.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Tabs 
              value={activeTower} 
              onChange={handleTowerChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: COLORS.SECONDARY },
                '& .MuiTab-root': { fontFamily: FONT_FAMILY, fontWeight: 600, color: COLORS.MUTED_TEXT, textTransform: 'none', fontSize: '15px' },
                '& .Mui-selected': { color: `${COLORS.SECONDARY} !important` },
              }}
            >
              {project.towers.map((tower) => (
                <Tab key={tower.name} label={tower.name} value={tower.name} />
              ))}
            </Tabs>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: "220px" }}>
                <Select
                  multiple
                  displayEmpty
                  value={selectedTowersToClone}
                  onChange={(e) => setSelectedTowersToClone(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>Clone Mapping To...</Typography>;
                    }
                    return <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{selected.length} Tower(s) Selected</Typography>;
                  }}
                  sx={{ borderRadius: "8px" }}
                >
                  <MenuItem disabled value="">
                    <em>Select Towers to Clone To</em>
                  </MenuItem>
                  {project.towers.filter(t => t.name !== activeTower).map((t) => (
                    <MenuItem key={t.name} value={t.name}>
                      <Checkbox checked={selectedTowersToClone.indexOf(t.name) > -1} />
                      <ListItemText primary={t.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleCloneToOtherTower}
                disabled={selectedTowersToClone.length === 0}
                sx={{
                  backgroundColor: COLORS.SECONDARY,
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  height: "40px",
                  "&:hover": { backgroundColor: "#3a2a50" }
                }}
              >
                Clone
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2 }}>
              <Tooltip title="Undo Last Action">
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleUndo}
                    disabled={historyIndex <= 0}
                    sx={{
                      color: COLORS.SECONDARY,
                      borderColor: COLORS.SECONDARY,
                      textTransform: "none",
                      fontWeight: 600,
                      height: "40px",
                      "&:hover": { backgroundColor: "#f0ecf6", borderColor: COLORS.SECONDARY }
                    }}
                  >
                    Undo
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Reset All Changes">
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleResetAll}
                    disabled={historyIndex <= 0}
                    sx={{
                      color: COLORS.ERROR,
                      borderColor: COLORS.ERROR,
                      textTransform: "none",
                      fontWeight: 600,
                      height: "40px",
                      "&:hover": { backgroundColor: "#ffebee", borderColor: COLORS.ERROR }
                    }}
                  >
                    Reset
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Divider />
        </Box>
      )}

      {/* 
        Project Type and Source Templates Selectors were conditionally displayed for "Custom Mapping" type or when no towers are selected.
        Now they are always visible to allow changing the project type and mapping template.
      */}

      {(true) && (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: `1px solid ${COLORS.BORDER || "#ECE7F2"}`,
          borderRadius: "12px",
          mb: 4,
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <FormControl sx={{ minWidth: "250px" }}>
            <InputLabel>Project Type</InputLabel>
            <Select
              value={projectType}
              label="Project Type"
              onChange={(e) => {
                const val = e.target.value;
                if (val === "Custom") {
                   setProjectType("Custom");
                   return; // do not overwrite template selection for "Custom" here
                }
                const match = masterMappings.find(
                  (m) => m.mappingName.toLowerCase() === val.toLowerCase(),
                );
                
                const updateMapping = () => {
                  setLoading(true);
                  setTimeout(() => {
                    setProjectType(val);
                    if (match) {
                      setSelectedSourceNames(match.mappingName);
                      const newComp = convertToUIState(match.components, [match], false);
                      startTransition(() => {
                        setComponents(newComp);
                        setHistory([newComp]);
                        setHistoryIndex(0);
                      });
                    }
                    setLoading(false);
                  }, 50);
                };

                if (components.length > 0) {
                  setTypeChangeConfirm({
                    open: true,
                    message: "Changing the project type will override your current mapping structure. Are you sure?",
                    onConfirm: updateMapping,
                  });
                } else {
                  updateMapping();
                }
              }}
              sx={{ borderRadius: "8px" }}
            >
              {projectTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
              <MenuItem value="Custom" sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>Custom</MenuItem>
            </Select>
          </FormControl>

          {projectType === "Custom" && (
            <FormControl sx={{ minWidth: "250px" }}>
              <TextField
                label="Custom Project Type"
                value={customProjectType}
                onChange={(e) => setCustomProjectType(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </FormControl>
          )}

          <FormControl sx={{ minWidth: "350px" }}>
            <InputLabel>Source Templates (Reference)</InputLabel>
            <Select
              value={selectedSourceNames}
              onChange={(e) => {
                const val = e.target.value;
                const match = masterMappings.find(
                  (m) => m.mappingName.toLowerCase() === val.toLowerCase(),
                );
                
                const updateSource = () => {
                  setSelectedSourceNames(val);
                  if (val === "Custom Mapping") {
                    startTransition(() => {
                      setComponents([]);
                      setHistory([[]]);
                      setHistoryIndex(0);
                    });
                  } else if (match) {
                    const newComp = convertToUIState(match.components, [match], false);
                    startTransition(() => {
                      setComponents(newComp);
                      setHistory([newComp]);
                      setHistoryIndex(0);
                    });
                  }
                };

                if (components.length > 0) {
                  setTypeChangeConfirm({
                    open: true,
                    message: "Changing the source template will override your current mapping structure. Are you sure?",
                    onConfirm: updateSource,
                  });
                } else {
                  updateSource();
                }
              }}
              label="Source Templates (Reference)"
              sx={{ borderRadius: "8px", textTransform: "capitalize" }}
            >
              {masterMappings.map((m) => (
                <MenuItem
                  key={m._id}
                  value={m.mappingName}
                  sx={{ textTransform: "capitalize" }}
                >
                  {m.mappingName}
                </MenuItem>
              ))}
              <MenuItem value="Custom Mapping" sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>Custom Mapping</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      )}

      {/* The Builder */}
      {(selectedSourceNames.length > 0 || loading) && (
        <Box sx={{ mt: 6 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, fontWeight: 700 }}
              >
                Dynamic Project Structure
              </Typography>
              {components.length > 0 && (
                <Tooltip title={isAllExpanded ? "Collapse All" : "Expand All"}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (isAllExpanded) {
                        setCollapseTrigger((prev) => prev + 1);
                      } else {
                        setExpandTrigger((prev) => prev + 1);
                      }
                      setIsAllExpanded(!isAllExpanded);
                    }}
                    sx={{
                      color: COLORS.SECONDARY,
                      backgroundColor: "#F0E9F7",
                      "&:hover": { backgroundColor: "#E6DAF3" },
                      width: 32,
                      height: 32,
                    }}
                  >
                    {isAllExpanded ? (
                      <KeyboardDoubleArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardDoubleArrowDownIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setStartScratchConfirm(true)}
                disabled={components.length === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  height: "40px",
                  borderRadius: "8px",
                }}
              >
                Start from Scratch
              </Button>
              <FormControl size="small" sx={{ minWidth: "180px" }}>
                <Select
                  multiple
                  displayEmpty
                  value={selectedMains}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.includes("custom")) {
                      handleAddCustomMain();
                      setSelectedMains(val.filter(v => v !== "custom"));
                    } else {
                      setSelectedMains(val);
                    }
                  }}
                  renderValue={() => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AddIcon sx={{ fontSize: "16px" }} />
                      <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                        Add Main Component
                      </Typography>
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { sx: { width: "250px", maxWidth: "250px" } } }}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: COLORS.SECONDARY,
                    color: "#fff",
                    ".MuiSvgIcon-root": { color: "#fff" },
                  }}
                >
                  {selectedSources
                    .flatMap((s) => s.components)
                    .map((opt) => (
                      <MenuItem key={opt._id} value={opt}>
                        <Checkbox checked={selectedMains.findIndex((m) => m._id === opt._id) > -1} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <ListItemText
                          primary={opt.title}
                          secondary={
                            selectedSources.length > 1
                              ? `Template: ${masterMappings.find((m) => m.components.some((c) => c._id === opt._id))?.mappingName}`
                              : ""
                          }
                        />
                      </MenuItem>
                    ))}
                  <Divider />
                  <MenuItem value="custom" sx={{ color: COLORS.PRIMARY, fontWeight: 600 }}>
                    + Add Custom Component
                  </MenuItem>
                  {selectedMains.length > 0 && (
                    <Box sx={{ p: 1, position: 'sticky', bottom: 0, bgcolor: 'white', zIndex: 1, borderTop: '1px solid #eee' }}>
                      <Button fullWidth variant="contained" size="small" onClick={(e) => {
                        e.stopPropagation();
                        handleAddComponent(selectedMains);
                        setSelectedMains([]);
                      }}>
                        Add {selectedMains.length} Selected
                      </Button>
                    </Box>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            {loading ? (
              <Box sx={{ width: '100%', mt: 4, mb: 4 }}>
                <LinearProgress sx={{ backgroundColor: COLORS.LIGHT_SECONDARY, '& .MuiLinearProgress-bar': { backgroundColor: COLORS.SECONDARY } }} />
              </Box>
            ) : (
              components.map((node, index) => (
                <DynamicNodeBuilder
                  key={node.id}
                  node={node}
                  isFirst={index === 0}
                  isLast={index === components.length - 1}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  sourceOptions={selectedSources}
                  onDelete={handleDeleteNode}
                  onDetailChange={handleDetailChange}
                  onAddSibling={handleAddSibling}
                  onAddChild={handleAddChild}
                  onAddCustomSibling={(id) => setCustomNodeDialog({ open: true, mode: "sibling", targetId: id, title: "" })} 
                  onAddCustomChild={(id) => setCustomNodeDialog({ open: true, mode: "child", targetId: id, title: "" })}
                  onCustomDetailChange={handleCustomDetailChange}
                  onAddCustomDetail={handleAddCustomDetail}
                  onRemoveCustomDetail={handleRemoveCustomDetail}
                  onRemoveMasterDetail={handleRemoveMasterDetail}
                  onTitleChange={handleTitleChange}
                  onCopy={handleCopyProjectNode}
                  expandTrigger={expandTrigger}
                  collapseTrigger={collapseTrigger}
                  masterNodeMap={masterNodeMap}
                  onOpenSort={handleOpenSort}
                />
              ))
            )}
          </Box>

          {components.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 6,
                gap: 2,
              }}
            >
              {isDraftLoaded && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    localStorage.removeItem(DRAFT_KEY);
                    window.location.reload();
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 3,
                  }}
                >
                  Discard Unsaved Changes
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: COLORS.SECONDARY,
                  color: COLORS.SECONDARY,
                  textTransform: "none",
                  px: 4,
                  fontWeight: 700,
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  backgroundColor: COLORS.SECONDARY,
                  textTransform: "none",
                  px: 6,
                  fontWeight: 700,
                  "&:hover": { backgroundColor: COLORS.PRIMARY },
                }}
              >
                {loading
                  ? "Saving..."
                  : projectId
                    ? "Update Mapping"
                    : "Save & Continue"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {selectedSourceNames.length === 0 && !loading && (
        <Box sx={{ mt: 10, textAlign: "center", opacity: 0.6 }}>
          <Icon name="sitemap" color={COLORS.GREY_COLOR} size={64} />
          <Typography
            sx={{ mt: 2, fontFamily: FONT_FAMILY, color: COLORS.GREY_COLOR }}
          >
            Select one or more Source Templates to begin building your project
            mapping.
          </Typography>
        </Box>
      )}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${deleteConfirm.node?.title}" and all its sub-items? This action cannot be undone.`}
        onConfirm={() => {
          executeDeleteNode(deleteConfirm.node?.id);
          setDeleteConfirm({ open: false, node: null });
        }}
        onCancel={() => setDeleteConfirm({ open: false, node: null })}
      />
      <ConfirmDialog
        open={typeChangeConfirm.open}
        title="Confirm Change"
        message={typeChangeConfirm.message}
        confirmText="Confirm"
        onConfirm={() => {
          if (typeChangeConfirm.onConfirm) {
            typeChangeConfirm.onConfirm();
          }
          setTypeChangeConfirm({ open: false, message: "", onConfirm: null });
        }}
        onCancel={() => setTypeChangeConfirm({ open: false, message: "", onConfirm: null })}
      />
      <ConfirmDialog
        open={startScratchConfirm}
        title="Start from Scratch"
        message="Are you sure you want to delete the current mapping and start from scratch? This action cannot be undone."
        confirmText="Start from Scratch"
        onConfirm={() => {
          updateComponents([]);
          setStartScratchConfirm(false);
        }}
        onCancel={() => setStartScratchConfirm(false)}
      />
      <Dialog
        open={cloneDialogOpen}
        onClose={() => !isCloning && setCloneDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1.5,
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            ...TYPOGRAPHY.H3,
            color: COLORS.PRIMARY,
            fontWeight: 700,
            fontFamily: FONT_FAMILY,
            pb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Clone Component</span>
          <IconButton size="small" onClick={() => setCloneDialogOpen(false)} disabled={isCloning} sx={{ color: COLORS.MUTED_TEXT }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {nodeToClone && (
            <Box>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_LARGE,
                  color: COLORS.TEXT,
                  fontWeight: 600,
                  mb: 2.5,
                }}
              >
                Cloning component: <span style={{ color: COLORS.SECONDARY }}>{nodeToClone.title}</span>
              </Typography>

              {/* Target Tower Dropdown */}
              <FormControl fullWidth size="small" disabled={isCloning} sx={{ mb: 2 }}>
                <InputLabel id="clone-tower-label">Target Tower</InputLabel>
                <Select
                  labelId="clone-tower-label"
                  value={cloneTargetTower}
                  label="Target Tower"
                  onChange={(e) => setCloneTargetTower(e.target.value)}
                  sx={{
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.BORDER,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.SECONDARY,
                    },
                  }}
                >
                  {(project?.towers || []).map((t) => (
                    <MenuItem key={t.name} value={t.name}>
                      {t.name} {t.name === activeTower ? "(Active)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Destination Dropdown */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: "12px", color: COLORS.MUTED_TEXT, mb: 0.5, ml: 0.5 }}>Target Destination Path</Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={isCloning}
                  onClick={(e) => setTargetAnchorEl(e.currentTarget)}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    justifyContent: "space-between",
                    textTransform: "none",
                    color: COLORS.TEXT,
                    borderColor: COLORS.BORDER,
                    borderRadius: "8px",
                    height: "40px",
                    px: 2,
                    "&:hover": { borderColor: COLORS.SECONDARY }
                  }}
                >
                  <Typography sx={{ fontSize: "14px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                    {cloneTargetParentId === "root" ? "[Root Level - Main Component]" : (
                      buildPathOptions(targetTowerComponents).find(o => o.id === cloneTargetParentId)?.path || "Select Target"
                    )}
                  </Typography>
                </Button>
                <Popover
                  open={Boolean(targetAnchorEl)}
                  anchorEl={targetAnchorEl}
                  onClose={() => setTargetAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: { width: targetAnchorEl ? targetAnchorEl.clientWidth : 300, maxHeight: 300, p: 1, mt: 0.5, borderRadius: "8px" }
                  }}
                >
                  <Box sx={{ p: 0.5 }}>
                    <Box
                      sx={{
                        display: "flex", alignItems: "center", py: 0.5, px: 1, cursor: "pointer",
                        bgcolor: cloneTargetParentId === "root" ? "rgba(110, 56, 150, 0.08)" : "transparent",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }, borderRadius: "4px", mb: 0.5
                      }}
                      onClick={() => { setCloneTargetParentId("root"); setTargetAnchorEl(null); }}
                    >
                      <Box sx={{ width: 24 }} />
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: COLORS.PRIMARY }}>[Root Level - Main Component]</Typography>
                    </Box>
                    {targetTowerComponents.map(node => (
                      <TargetNodeItem
                        key={node.id}
                        node={node}
                        invalidIds={getDescendantIds(nodeToClone)}
                        selectedId={cloneTargetParentId}
                        onSelect={(id) => { setCloneTargetParentId(id); setTargetAnchorEl(null); }}
                        depth={0}
                      />
                    ))}
                  </Box>
                </Popover>
              </Box>

              {/* Clone Method Toggle */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.PRIMARY, mb: 1 }}>
                  Clone Method
                </Typography>
                <RadioGroup
                  row
                  value={cloneMethod}
                  onChange={(e) => setCloneMethod(e.target.value)}
                >
                  <FormControlLabel
                    value="create"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: "14px" }}>Create New Copies</Typography>}
                    disabled={isCloning}
                  />
                  <FormControlLabel
                    value="existing"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: "14px" }}>Apply to Existing Components</Typography>}
                    disabled={isCloning}
                  />
                </RadioGroup>
              </Box>

              {cloneMethod === "create" ? (
                <>
                  {/* Number of Clones */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Number of Clones"
                    type="number"
                    disabled={isCloning}
                    value={cloneCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      setCloneCount(isNaN(val) || val < 1 ? 1 : val);
                    }}
                    InputProps={{ inputProps: { min: 1, max: 100 } }}
                    sx={{
                      mb: 3,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  />

                  {/* Live Preview of Names */}
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#F9FAFB",
                      border: `1px dashed ${COLORS.LIGHT_SECONDARY || "#C5A9D9"}`,
                      borderRadius: "10px",
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_SMALL,
                        color: COLORS.MUTED_TEXT,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: 1,
                      }}
                    >
                      Live Preview of Generated Names (Editable):
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {generatedNames.map((title, index) => (
                        <TextField
                          key={index}
                          value={title}
                          onChange={(e) => {
                            const newNames = [...generatedNames];
                            newNames[index] = e.target.value;
                            setGeneratedNames(newNames);
                          }}
                          size="small"
                          sx={{
                            minWidth: "80px",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "6px",
                              backgroundColor: "#fff",
                              height: "32px",
                              ...TYPOGRAPHY.BODY_SMALL,
                              fontWeight: 600,
                              color: COLORS.PRIMARY,
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#F9FAFB",
                    border: `1px solid ${COLORS.BORDER || "#E5E5E5"}`,
                    borderRadius: "10px",
                    mb: 1,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.MUTED_TEXT,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1,
                    }}
                  >
                    Select target components:
                  </Typography>
                  <FormGroup>
                    {(() => {
                      let availableSiblings = [];
                      if (cloneTargetParentId === "root") {
                        availableSiblings = targetTowerComponents;
                      } else {
                        const findSiblings = (items) => {
                          for (const item of items) {
                            if (item.id === cloneTargetParentId) {
                              availableSiblings = item.subItems || [];
                              return;
                            }
                            findSiblings(item.subItems || []);
                          }
                        };
                        findSiblings(targetTowerComponents);
                      }
                      
                      const validTargets = availableSiblings.filter(s => s.id !== nodeToClone.id);
                      
                      if (validTargets.length === 0) {
                         return <Typography sx={{ fontSize: "13px", fontStyle: "italic", color: "text.secondary" }}>No other components available at this level.</Typography>;
                      }

                      return validTargets.map(target => (
                        <FormControlLabel
                          key={target.id}
                          control={
                            <Checkbox 
                              size="small" 
                              checked={selectedExistingTargets.includes(target.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedExistingTargets(prev => [...prev, target.id]);
                                } else {
                                  setSelectedExistingTargets(prev => prev.filter(id => id !== target.id));
                                }
                              }}
                            />
                          }
                          label={<Typography sx={{ fontSize: "14px" }}>{target.title}</Typography>}
                        />
                      ));
                    })()}
                  </FormGroup>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setCloneDialogOpen(false)}
            disabled={isCloning}
            sx={{
              borderColor: COLORS.SECONDARY,
              color: COLORS.SECONDARY,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeClone}
            disabled={isCloning}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 4,
              minWidth: "160px",
              height: "40px",
              "&:hover": {
                backgroundColor: COLORS.PRIMARY,
              },
            }}
          >
            {isCloning ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Clone Component"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Custom Component Title */}
      <Dialog
        open={customNodeDialog.open}
        onClose={() => setCustomNodeDialog({ open: false, mode: "", targetId: null, title: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
          Custom Component Name
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: "13px", color: COLORS.TEXT, mb: 1.5 }}>
              Enter a name for your custom element:
            </Typography>
            <TextField
              fullWidth
              autoFocus
              variant="outlined"
              size="small"
              placeholder="e.g., Special Area"
              value={customNodeDialog.title}
              onChange={(e) =>
                setCustomNodeDialog({ ...customNodeDialog, title: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCustomDialogSubmit();
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCustomNodeDialog({ open: false, mode: "", targetId: null, title: "" })}
            sx={{ color: COLORS.MUTED_TEXT, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomDialogSubmit}
            disabled={!customNodeDialog.title.trim()}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              borderRadius: "6px",
              "&:hover": { backgroundColor: COLORS.PRIMARY },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sort Dialog */}
      <Dialog
        open={sortDialogState.open}
        onClose={() => setSortDialogState({ open: false, items: [], parentId: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            padding: "8px",
            backgroundColor: "#FAFAFA"
          },
        }}
      >
        <DialogTitle sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
          Sort Level Items
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: "13px", color: COLORS.TEXT, mb: 2 }}>
              Drag and drop the items below to reorder them.
            </Typography>
            <DndContext
              sensors={useSensors(
                useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
                useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
              )}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={sortDialogState.items.map(item => item.id)} strategy={verticalListSortingStrategy}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1 }}>
                  {sortDialogState.items.map((item) => (
                    <SortableItem key={item.id} id={item.id} title={item.title} />
                  ))}
                  {sortDialogState.items.length === 0 && (
                    <Typography sx={{ fontSize: "13px", fontStyle: "italic", color: COLORS.MUTED_TEXT, p: 2, textAlign: "center" }}>
                      No items to sort.
                    </Typography>
                  )}
                </Box>
              </SortableContext>
            </DndContext>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSortDialogState({ open: false, items: [], parentId: null })}
            sx={{ color: COLORS.MUTED_TEXT, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplySort}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              borderRadius: "6px",
              "&:hover": { backgroundColor: COLORS.PRIMARY },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StartProjectMapping;
